import type { TFunction } from "i18next";

const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * Mes en 3 letras en el idioma dado ("Sep", "Ene"...). Intl devuelve
 * "sept." en español, así que se recorta a 3 letras.
 */
export const shortMonth = (month: number, lang: string) => {
  const name = new Date(2000, month - 1, 1)
    .toLocaleDateString(lang, { month: "short" })
    .replace(".", "")
    .slice(0, 3);
  return name.charAt(0).toUpperCase() + name.slice(1);
};

/** Mes completo en el idioma dado ("Septiembre", "September"). */
export const longMonth = (month: number, lang: string) => {
  const name = new Date(2000, month - 1, 1).toLocaleDateString(lang, {
    month: "long",
  });
  return name.charAt(0).toUpperCase() + name.slice(1);
};

/** "Sep 26" */
export const shortMonthYear = (month: number, year: number, lang: string) =>
  `${shortMonth(month, lang)} ${String(year).slice(-2)}`;

const toDate = (iso?: string | null) => (iso ? new Date(iso) : null);

const isSameMonth = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

/**
 * Primera línea de un rango de fechas: "del 3 al 28" si empieza y acaba en el
 * mismo mes, "del 28/08 al 03/09" si no. Null si no hay ninguna fecha.
 */
export const formatDayRange = (
  start: string | null | undefined,
  end: string | null | undefined,
  t: TFunction
): string | null => {
  const from = toDate(start);
  const to = toDate(end);
  const sameMonth = !!from && !!to && isSameMonth(from, to);
  const fmt = (d: Date) =>
    sameMonth
      ? String(d.getDate())
      : `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;
  if (from && to)
    return t("briefings.dateRange", "{{from}} to {{to}}", {
      from: fmt(from),
      to: fmt(to),
    });
  if (from)
    return t("briefings.dateFrom", "from {{from}}", { from: fmt(from) });
  if (to) return t("briefings.dateTo", "until {{to}}", { to: fmt(to) });
  return null;
};

/**
 * Segunda línea de un rango de fechas: "Sep 26", "Ago – Sep 26" o
 * "Dic 25 – Ene 26" según abarque uno o varios meses/años.
 */
export const formatMonthSpan = (
  start: string | null | undefined,
  end: string | null | undefined,
  lang: string
): string | null => {
  const from = toDate(start);
  const to = toDate(end) ?? from;
  const first = from ?? to;
  if (!first || !to) return null;
  const my = (d: Date) =>
    shortMonthYear(d.getMonth() + 1, d.getFullYear(), lang);
  if (isSameMonth(first, to)) return my(first);
  if (first.getFullYear() === to.getFullYear())
    return `${shortMonth(first.getMonth() + 1, lang)} – ${my(to)}`;
  return `${my(first)} – ${my(to)}`;
};

/** "26/09/26" (dd/mm/yy) whatever the UI language. "" if there is no date. */
export const formatShortDate = (iso: string | null | undefined): string => {
  const d = toDate(iso);
  if (!d) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${String(
    d.getFullYear()
  ).slice(-2)}`;
};
